// Global style constants and utility functions — CONFORM.TOOLS DataCalc
// Updated for the CONFORM.TOOLS brand identity with red/yellow accent palette

export const colors = {
  labelColor: 'text-ink',
  bgColor: 'bg-surface-card',
  inputBgColor: 'bg-input-bg',
  lockedBgColor: 'bg-conform-red/5',
  affectedBgColor: 'bg-input-bg',
  readOnlyBgColor: 'bg-semantic-gray-100',
  inputBorderColor: 'border-input-border',
  affectedBorderColor: 'border-conform-red/30',
  lockedBorderColor: 'border-conform-red',
  textColor: 'text-ink',
  buttonColor: 'bg-conform-red-deep hover:bg-conform-red-accent',
  buttonBorderColor: 'border-conform-red',
  arrowColor: 'text-conform-red',
  buttonShadow: 'shadow-sm hover:shadow-md',
  lockButtonBgColor: 'bg-semantic-gray-100 hover:bg-semantic-gray-200',
  lockButtonActiveBgColor: 'bg-conform-red/5',
  lockIconColor: 'text-conform-red',
  unlockIconColor: 'text-ink-tertiary',
  resultText: 'text-ink font-medium',
};

export const spacing = {
  desktop: {
    rowGap: 'space-y-2',
    sectionGap: 'space-y-4',
    fieldGap: 'mb-1',
    padding: 'p-1',
    margin: 'm-4',
    gridGap: 'gap-2',
    headerPadding: 'px-4 py-2',
    contentPadding: 'p-5',
  },
  mobile: {
    rowGap: 'space-y-3',
    sectionGap: 'space-y-4',
    fieldGap: 'mb-1',
    padding: 'p-1',
    margin: 'm-2',
    gridGap: 'gap-2',
    headerPadding: 'px-2 py-1',
    contentPadding: 'p-2',
  }
};

export const layout = {
  grid: {
    twoColumns: 'grid-cols-2',
    threeColumns: 'grid-cols-3',
    fourColumns: 'grid-cols-4',
    lockLayout: 'grid-cols-[1fr,auto,1fr]',
    daysLayout: 'grid-cols-[1fr,100px,1fr]',
    brandCamera: 'grid-cols-[1fr,2fr]',
    codecBitrate: 'grid-cols-[2fr,1fr]',
    resolutionFramerate: 'grid-cols-[2fr,1fr]',
    dailesAspectRatio: 'grid-cols-[1fr,1fr,auto,1fr]',
    filmFormatRow: 'grid-cols-[30fr,20fr,50fr]',
    filmRollsRow: 'grid-cols-[1fr,1fr,1fr]',
    filmPriceRow: 'grid-cols-[2fr,1fr]',
  },
  flex: {
    standard: 'flex items-center',
    between: 'flex justify-between',
    column: 'flex flex-col',
    wrap: 'flex flex-wrap'
  }
};

export const typography = {
  desktop: {
    label: 'text-sm font-medium',
    input: 'text-base',
    heading: 'text-lg font-semibold'
  },
  mobile: {
    label: 'text-xs font-medium',
    input: 'text-base',
    heading: 'text-base font-semibold'
  }
};

const baseInputStyle = `
  w-full 
  px-3 
  py-2 
  border 
  rounded-lg 
  h-[40px] 
  font-sans
  text-center
  text-sm
  md:text-base
  transition-colors
  duration-150
`;

export const commonStyles = {
  commonInputClass: `
    ${baseInputStyle}
    ${colors.inputBgColor} 
    ${colors.inputBorderColor} 
    ${colors.textColor}
    focus:outline-none
    focus:ring-2
    focus:ring-conform-red
    focus:border-conform-red
    [-webkit-appearance:none]
    [appearance:none]
    text-align: center !important
    -moz-text-align: center !important
    -webkit-text-align: center !important
    text-align-last: center !important
    -moz-text-align-last: center !important
    -webkit-text-align-last: center !important
  `,

  lockButtonStyle: (isActive) => `p-2 rounded-lg h-[40px] w-[42px] flex items-center justify-center self-end ${colors.lockButtonBgColor} border-2 ${colors.buttonShadow} transition-colors duration-150 ${
    isActive ? 'border-conform-red bg-conform-red/5' : 'border-border'
  }`,

  baseStyle: "w-full px-3 py-2 border rounded-lg h-[40px] appearance-none",

  getInputStyle: (field, calculationMode) => {
    if (calculationMode === field) {
      return `${baseInputStyle} 
        ${colors.lockedBgColor} 
        border-2 
        ${colors.lockedBorderColor} 
        text-ink-secondary 
        cursor-not-allowed 
        pointer-events-none`;
    } else if (calculationMode) {
      return `${baseInputStyle} 
        ${colors.affectedBgColor} 
        border-[1.5px] 
        ${colors.affectedBorderColor} 
        text-ink`;
    }
    
    return `${baseInputStyle} 
      ${colors.inputBgColor} 
      ${colors.inputBorderColor} 
      ${colors.textColor}
      focus:outline-none
      focus:ring-2
      focus:ring-conform-red
      focus:border-conform-red`;
  },

  textCenterImportant: {
    textAlign: 'center !important',
    textAlignLast: 'center !important',
    '-moz-text-align-last': 'center !important',
    '-webkit-text-align-last': 'center !important'
  },

  actionButtons: {
    container: "mt-4 grid grid-cols-3 sm:grid-cols-4 w-full gap-2 sm:flex sm:justify-between",
    button: (color = colors.buttonColor) => `
      px-2 sm:px-4 rounded-lg text-white 
      flex items-center justify-center 
      w-full
      sm:min-w-[100px]
      sm:flex-1
      py-2.5
      transition-all duration-150
      ${color} ${colors.buttonShadow}
    `,
    buttonIcon: "",
    buttonText: "text-sm sm:text-base font-medium whitespace-nowrap",
    copyButton: "hidden sm:flex",
    spacing: {
      first: "",
      middle: "",
      last: ""
    }
  },

  readOnlyInput: `${colors.readOnlyBgColor} cursor-default`,

  labelClass: `
    block 
    text-ink-secondary 
    text-sm 
    font-medium 
    font-sans
    sm:text-xs 
    md:text-sm
    text-left
    mb-1
  `
};

export const breakpoints = {
  mobile: 767,
  tablet: 1024,
  desktop: 1025
};

export const deviceTypes = {
  isMobile: () => window.innerWidth <= breakpoints.mobile,
  isTablet: () => {
    const width = window.innerWidth;
    return width > breakpoints.mobile && width <= breakpoints.tablet;
  },
  isTouchDevice: () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
  isDesktop: () => window.innerWidth > breakpoints.tablet
};

export const headerStyles = {
  container: (isMobile) => `
    flex items-center justify-between 
    ${isMobile ? 'px-3 py-2' : 'px-5 py-3'}
    bg-conform-red shadow-md shadow-black/20
  `,
  title: (isMobile) => `
    ${isMobile ? 'text-sm hidden sm:block' : 'text-lg'} 
    font-headline font-bold text-conform-black
  `,
  iconContainer: `
    flex items-center space-x-2
    sm:space-x-3
    md:space-x-4
  `,
  icon: (isMobile) => `
    ${isMobile ? 'w-8 h-8' : 'w-10 h-10'}
    p-1.5
    rounded-full
    hover:bg-conform-black/10
    transition-colors
    duration-200
  `,
  select: (isCompactView) => `
    text-${isCompactView ? 'base' : 'lg'} 
    font-semibold 
    bg-surface-card 
    border border-border
    rounded-lg
    focus:outline-none 
    focus:ring-2
    focus:ring-conform-red
    w-full 
    text-right
    appearance-none
    pr-8
    pl-4
    py-2
    transition-colors
    duration-150
  `,
  compactContainer: `
    flex 
    flex-col 
    justify-between 
    flex-grow 
    pl-4
  `,
  iconContainer: `
    flex-1 
    flex 
    items-center 
    justify-end
  `,
  iconGrid: `
    w-full 
    flex 
    justify-between 
    items-center 
    px-2
  `,
  icon: (isActive) => `
    transition-colors 
    flex 
    justify-center
    ${isActive ? 'text-conform-red' : 'text-ink-tertiary'}
  `
};
